import "./SpinningCoin.css";

interface SpinningCoinProps {
  size?: number | string;
  thickness?: number;
  edgeSlices?: number;
  className?: string;
}

const TICKS = Array.from({ length: 60 }, (_, i) => {
  const a = (i * 6 * Math.PI) / 180;
  const long = i % 5 === 0;
  const r1 = long ? 90 : 92;
  const r2 = 95;
  return {
    key: i,
    x1: 100 + r1 * Math.cos(a),
    y1: 100 + r1 * Math.sin(a),
    x2: 100 + r2 * Math.cos(a),
    y2: 100 + r2 * Math.sin(a),
    width: long ? 1 : 0.5,
  };
});

function CoinFaceEtching(): JSX.Element {
  return (
    <svg className="coin-etch" viewBox="0 0 200 200" aria-hidden="true">
      <g fill="none" stroke="rgba(255,255,255,0.95)" strokeWidth="0.5">
        <circle cx="100" cy="100" r="92" />
        <circle cx="100" cy="100" r="86" strokeDasharray="0.6 1.2" />
        <circle cx="100" cy="100" r="76" />
        <circle cx="100" cy="100" r="60" strokeDasharray="2 2" />
        <circle cx="100" cy="100" r="40" />
      </g>
      <g stroke="rgba(255,255,255,0.95)">
        {TICKS.map((t) => (
          <line
            key={t.key}
            x1={t.x1.toFixed(2)}
            y1={t.y1.toFixed(2)}
            x2={t.x2.toFixed(2)}
            y2={t.y2.toFixed(2)}
            strokeWidth={t.width}
          />
        ))}
      </g>
      <g fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="0.3">
        <polygon points="100,72 124,86 124,114 100,128 76,114 76,86" />
        <polygon points="100,82 116,91 116,109 100,118 84,109 84,91" />
      </g>
      <g
        fill="rgba(255,255,255,0.95)"
        fontFamily='"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace'
        fontSize="4"
        letterSpacing="1.4"
        fontWeight="600"
      >
        <text x="100" y="14" textAnchor="middle">FLEX FUND</text>
        <text x="100" y="192" textAnchor="middle">TRADABLE ASSETS</text>
        <text x="14" y="102" textAnchor="middle" transform="rotate(-90 14 102)">
          · · ·
        </text>
        <text x="186" y="102" textAnchor="middle" transform="rotate(90 186 102)">
          · · ·
        </text>
      </g>
    </svg>
  );
}

interface CoinFaceProps {
  side: "front" | "back";
  thickness: number;
}

function CoinFace({ side, thickness }: CoinFaceProps): JSX.Element {
  const z = side === "front" ? thickness / 2 : -thickness / 2;
  const transform = side === "front" ? `translateZ(${z}px)` : `translateZ(${z}px) rotateY(180deg)`;
  return (
    <div className={`coin-face coin-face-${side}`} style={{ transform }} aria-hidden="true">
      <CoinFaceEtching />
      <div className="coin-sheen" aria-hidden="true" />
      <img
        src="/logo-512.png"
        alt=""
        aria-hidden="true"
        className="coin-logo"
        draggable={false}
      />
    </div>
  );
}

export default function SpinningCoin({
  size = 360,
  thickness = 24,
  edgeSlices = 20,
  className,
}: SpinningCoinProps): JSX.Element {
  const slices = Array.from({ length: edgeSlices }, (_, i) => {
    const z = -thickness / 2 + (i + 0.5) * (thickness / edgeSlices);
    const t = (i / (edgeSlices - 1)) * 2 - 1;
    const scale = 1 - Math.pow(Math.abs(t), 2) * 0.012;
    const shade = 1 - Math.pow(Math.abs(t), 2) * 0.22;
    return (
      <div
        key={i}
        className="coin-slice"
        style={{
          transform: `translateZ(${z}px) scale(${scale.toFixed(4)})`,
          filter: `brightness(${shade.toFixed(3)})`,
        }}
        aria-hidden="true"
      />
    );
  });

  const dim = typeof size === "number" ? `${size}px` : size;
  return (
    <div
      className={["coin-stage", className].filter(Boolean).join(" ")}
      style={{ width: dim, height: dim }}
      aria-hidden="true"
    >
      <div className="coin-spin" aria-hidden="true">
        <div className="coin-edge" aria-hidden="true">
          {slices}
        </div>
        <CoinFace side="front" thickness={thickness} />
        <CoinFace side="back" thickness={thickness} />
      </div>
    </div>
  );
}
